/**
 * Based off of the `openseadragon-opacity-slider plugin
 * https://github.com/WasMachenSachen/openseadragon-opacity-slider
 */
import OpenSeadragon from "openseadragon";

const pluginName = "OpenSeadragon Opacity Slider";

export interface LayerInformation {
  source: string | OpenSeadragon.TileSource;
  layerName: string;
  includeSlider: boolean;
  hidden?: boolean;
}

export interface OpacitySliderOptions {
  viewer: OpenSeadragon.Viewer;
  layerInformation: LayerInformation[];
  layerPickerHeading?: string;
  showRangeSlider?: boolean;
  showNumberInput?: boolean;
  sortable?: boolean;
  showLayerPicker?: boolean;
  showRemove?: boolean;
  showlayerInformation?: boolean;
  onChange?: (opacity: number, layerIndex: number) => void;
  onMarkupBuilt?: () => void;
}

declare module "openseadragon" {
  interface Viewer {
    opacityslider(options?: Omit<OpacitySliderOptions, "viewer">): void;
    opacitysliderInstance?: object;
  }
}

export const osd = (function ($: typeof OpenSeadragon): typeof OpenSeadragon {
  if (!$.version || $.version.major < 2) {
    throw new Error(
      `This version of ${pluginName} requires OpenSeadragon version 2.0.0+`
    );
  }

  $.Viewer.prototype.opacityslider = function (
    this: OpenSeadragon.Viewer,
    options?: Omit<OpacitySliderOptions, "viewer">
  ): void {
    if (!this.opacitysliderInstance) {
      const fullOptions = {
        ...(options ?? {}),
        viewer: this,
      } as OpacitySliderOptions;
      this.opacitysliderInstance = new OpacitySliderPlugin(fullOptions);
    }
  };

  function OpacitySliderPlugin(options: OpacitySliderOptions): void {
    if (!options.layerInformation || !Array.isArray(options.layerInformation)) {
      throw new Error(`${pluginName} requires options.layerInformation`);
    }

    if (!document.getElementById("opacitySliderWrapper")) {
      throw new Error(
        `${pluginName} requires a div with an id of 'opacitySliderWrapper'`
      );
    }

    options.layerInformation.forEach((el) => {
      if (el.hidden === undefined) el.hidden = false;
    });

    options.layerPickerHeading = options.layerPickerHeading ?? "Choose layers:";

    const opacitySliderDiv = document.createElement("div");
    opacitySliderDiv.id = "opacitySliderWrapperContent";
    document
      .getElementById("opacitySliderWrapper")!
      .appendChild(opacitySliderDiv)
      .classList.add("osdos-opacity-layer-slider-wrapper");

    options.viewer.world.addHandler("add-item", function (event) {
      const item = event.item as OpenSeadragon.TiledImage & {
        source: { tilesUrl?: string; url?: string };
        osdosHidden: boolean;
      };
      const itemUrl = item.source.tilesUrl
        ? `${item.source.tilesUrl.slice(0, -7)}.dzi`
        : item.source.url ?? "";
      const affiliatedLayer = options.layerInformation.find(
        (el) => el.source === itemUrl
      );
      item.osdosHidden = affiliatedLayer?.hidden ?? false;
      buildMarkup(options);
    });
  }

  function buildMarkup(options: OpacitySliderOptions): void {
    const orderedLayerInformation = options.layerInformation;
    const opacitySliderContent = document.getElementById(
      "opacitySliderWrapperContent"
    );
    if (!opacitySliderContent) return;
    opacitySliderContent.innerHTML = "";

    orderedLayerInformation.forEach((el, index) => {
      const sliderWrapper = document.createElement("div");
      sliderWrapper.className = "osdos-layer-slider";
      sliderWrapper.setAttribute("data-osd-index", String(index));
      sliderWrapper.style.cssText =
        "display: flex; flex-direction: row; gap: 8px; color: white;";

      const layerName = document.createElement("label");
      layerName.innerHTML = el.layerName;
      layerName.setAttribute("for", `opacity-slider__${index}`);
      layerName.style.cssText = "flex-shrink: 0; flex-grow: 0;";
      layerName.classList.add("osodos-layer-name-wrapper");
      sliderWrapper.appendChild(layerName);

      if (options.showRangeSlider !== false) {
        const layerOpacitySlider = document.createElement("input");
        const attributes: Record<string, string> = {
          "data-osdos": "layer-opacity-slider",
          type: "range",
          min: "0",
          max: "100",
          name: "opacity-slider",
          class: "osdos-layer-slider-range-input",
          id: `opacity-slider__${index}`,
          style: "flex-grow: 1; flex-shrink: 1;",
        };
        Object.entries(attributes).forEach(([attr, val]) => {
          layerOpacitySlider.setAttribute(attr, val);
        });
        sliderWrapper.appendChild(layerOpacitySlider);
      }

      if (!el.includeSlider) {
        sliderWrapper.style.display = "none";
      }

      opacitySliderContent.appendChild(sliderWrapper);
    });

    const opacitySliderWrapper = document.getElementById(
      "opacitySliderWrapper"
    );
    if (!opacitySliderWrapper) return;
    opacitySliderWrapper.innerHTML = "";
    opacitySliderWrapper.appendChild(opacitySliderContent);

    document
      .querySelectorAll<HTMLInputElement>("[data-osdos='layer-opacity-slider']")
      .forEach((el, index) => {
        el.setAttribute("data-osd-index", String(index));
        (["change", "input"] as const).forEach((eventName) => {
          el.addEventListener(eventName, (event) => {
            const opacity =
              Number((event.target as HTMLInputElement).value) / 100;
            setOpacity(index, opacity, options);
          });
        });
      });

    orderedLayerInformation.forEach((_, index) => {
      const currentItem = options.viewer.world.getItemAt(index);
      if (!currentItem) return;

      const currentOpacity = currentItem.getOpacity();

      if (options.showNumberInput !== false) {
        const numberInput = document.querySelector<HTMLInputElement>(
          `[data-osdos='layer-opacity-slider-value'][data-osd-index='${index}']`
        );
        if (numberInput) numberInput.value = String(currentOpacity * 100);
      }
      if (options.showRangeSlider !== false) {
        const rangeInput = document.querySelector<HTMLInputElement>(
          `[data-osdos='layer-opacity-slider'][data-osd-index='${index}']`
        );
        if (rangeInput) rangeInput.value = String(currentOpacity * 100);
      }
    });

    disableButtons(options);
    options.onMarkupBuilt?.();
  }

  function setOpacity(
    index: number,
    opacity: number,
    options: OpacitySliderOptions
  ): void {
    options.viewer.world.getItemAt(index)?.setOpacity(opacity);
    options.onChange?.(opacity, index);
  }

  function disableButtons(options: OpacitySliderOptions): void {
    document
      .querySelectorAll<HTMLElement>(".opacity-slider-disabled")
      .forEach((el) => {
        el.classList.remove("osdos-curser-not-allowed");
        (el as HTMLButtonElement).disabled = false;
      });

    const firstElement = document.querySelector<HTMLButtonElement>(
      "[data-osd-index='0'] [data-osdos='down-button']"
    );
    if (firstElement !== null) {
      firstElement.classList.add("osdos-curser-not-allowed");
      firstElement.disabled = true;
    }

    const lastElement = document.querySelector<HTMLButtonElement>(
      `[data-osd-index='${
        options.viewer.world.getItemCount() - 1
      }'] [data-osdos='up-button']`
    );
    if (lastElement !== null) {
      lastElement.classList.add("osdos-curser-not-allowed");
      lastElement.disabled = true;
    }
  }

  return $;
})(OpenSeadragon);
